"use client";

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import Link from "next/link";
import {
  AnimatePresence,
  motion,
  useInView,
  useReducedMotion,
} from "framer-motion";
import {
  ArrowRight,
  Check,
  Gift,
  Loader2,
  Search,
  ShieldCheck,
  X,
} from "lucide-react";
import QRCode from "react-qr-code";

import { trackEvent } from "@/lib/analytics";
import { useSession } from "@/lib/auth-client";
import { publicApi } from "@/lib/dpop-client";
import { FEXR_APP_URL } from "@/lib/fexr-wallet";
import {
  getPendingReferralCode,
  PENDING_REFERRAL_EVENT,
} from "@/lib/pending-referral";
import { brandedLinkUrl, getOneLink } from "@/lib/share-links";
import type { PlatformStats } from "@/lib/types/engagement";
import { cn } from "@/lib/utils";
import { PLANS_URL } from "@/hooks/use-account-pulse";
import {
  useTokenizedStocks,
  type TokenizedStock,
  type TokenizedStocksResponse,
} from "@/hooks/use-market-data";
import {
  formatReferralUsd,
  usePublicReferralStats,
} from "@/hooks/use-referrals";
import { Kbd } from "@/components/ui/kbd";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AnimateOnScroll,
  EASE_OUT,
  RadarDot,
} from "@/components/marketing/animate-on-scroll";
import { LiveActivityTicker } from "@/components/marketing/live-activity-ticker";
import { MarketDataShowcase } from "@/components/marketing/market-data-showcase";
import { AssetIcon } from "@/components/shared/asset-icon";

export type { PlatformStats };

// ─── ReferralInviteBanner ─────────────────────────────────────────────────────
// Shown to signed-out visitors while a captured referral code is stored
// (PendingReferralApplier captures ?join=CODE and applies it after sign-up).
// Amounts come from the public program terms.

function ReferralInviteBanner({ isAuthed }: { isAuthed: boolean }) {
  const { terms } = usePublicReferralStats();
  const [code, setCode] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const sync = () => setCode(getPendingReferralCode());
    sync();
    window.addEventListener(PENDING_REFERRAL_EVENT, sync);
    return () => window.removeEventListener(PENDING_REFERRAL_EVENT, sync);
  }, []);

  if (!code || isAuthed || dismissed) return null;

  return (
    <div className="container max-w-6xl pt-4">
      <div className="flex flex-col gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Gift className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">
              A friend invited you
            </p>
            <p className="text-xs text-muted-foreground">
              Deposit {formatReferralUsd(terms.joining_perk_deposit_usd)} and
              get {terms.perk_days} days of Fexr Plus.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Link
            href="/register"
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            Create account
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss invite"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

type HeroChartPoint = readonly [x: number, y: number];

const HERO_TITLE_CHARTS: readonly (readonly HeroChartPoint[])[] = [
  // Higher highs and higher lows.
  [
    [0, 1],
    [0.06, 0.9],
    [0.12, 0.94],
    [0.19, 0.75],
    [0.26, 0.82],
    [0.34, 0.61],
    [0.41, 0.69],
    [0.49, 0.47],
    [0.56, 0.56],
    [0.65, 0.33],
    [0.73, 0.42],
    [0.81, 0.21],
    [0.89, 0.29],
    [0.96, 0.07],
    [1, 0],
  ],
  // Tight accumulation followed by a breakout and retest.
  [
    [0, 1],
    [0.07, 0.91],
    [0.14, 0.96],
    [0.22, 0.87],
    [0.29, 0.94],
    [0.37, 0.85],
    [0.44, 0.9],
    [0.51, 0.82],
    [0.56, 0.53],
    [0.64, 0.64],
    [0.7, 0.48],
    [0.77, 0.25],
    [0.84, 0.34],
    [0.91, 0.13],
    [0.96, 0.2],
    [1, 0],
  ],
  // Double-bottom reversal through its neckline.
  [
    [0, 1],
    [0.08, 0.76],
    [0.16, 0.87],
    [0.24, 0.66],
    [0.32, 0.93],
    [0.4, 0.7],
    [0.49, 0.48],
    [0.57, 0.61],
    [0.65, 0.36],
    [0.74, 0.46],
    [0.82, 0.22],
    [0.9, 0.3],
    [1, 0],
  ],
  // Impulse move, descending flag, then continuation.
  [
    [0, 1],
    [0.05, 0.9],
    [0.1, 0.68],
    [0.16, 0.43],
    [0.24, 0.5],
    [0.31, 0.47],
    [0.38, 0.57],
    [0.45, 0.53],
    [0.53, 0.64],
    [0.6, 0.54],
    [0.67, 0.28],
    [0.75, 0.36],
    [0.83, 0.13],
    [0.91, 0.21],
    [1, 0],
  ],
  // Volatile recovery with sharp, uneven liquidity swings.
  [
    [0, 1],
    [0.06, 0.69],
    [0.12, 0.84],
    [0.18, 0.54],
    [0.25, 0.77],
    [0.31, 0.47],
    [0.38, 0.66],
    [0.45, 0.39],
    [0.52, 0.59],
    [0.59, 0.29],
    [0.66, 0.49],
    [0.73, 0.21],
    [0.8, 0.37],
    [0.87, 0.1],
    [0.93, 0.22],
    [1, 0],
  ],
] as const;

type HeroPt = { x: number; y: number };

// Seconds each chart design stays on screen before the next one draws in.
const HERO_CHART_CYCLE = 5.4;
const HERO_DRAW_EASE = [0.65, 0, 0.35, 1] as const;
// Theme tokens, so the hero follows Settings > Appearance like the app.
const HERO_UP = "hsl(var(--success))";
const HERO_DOWN = "hsl(var(--danger))";
const HERO_AMBER = "hsl(var(--warning))";

function heroLinePath(points: HeroPt[]) {
  return points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(" ");
}

// Catmull-Rom → cubic Bézier, for the organic "recovery" design.
function heroSmoothPath(points: HeroPt[]) {
  if (points.length < 2) return "";
  let d = `M ${points[0]!.x.toFixed(2)} ${points[0]!.y.toFixed(2)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i]!;
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const p3 = points[i + 2] ?? p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return d;
}

// Screen space: a "low" is a larger y than both neighbours.
function heroPivots(points: HeroPt[], kind: "high" | "low") {
  const out: number[] = [];
  for (let i = 1; i < points.length - 1; i++) {
    const { y } = points[i]!;
    const a = points[i - 1]!.y;
    const b = points[i + 1]!.y;
    if (kind === "low" ? y > a && y > b : y < a && y < b) out.push(i);
  }
  return out;
}

function heroLineThrough(a: HeroPt, b: HeroPt, x0: number, x1: number) {
  const slope = (b.y - a.y) / (b.x - a.x || 1);
  const at = (x: number) => a.y + slope * (x - a.x);
  return { x0, y0: at(x0), x1, y1: at(x1) };
}

interface HeroChartProps {
  points: HeroPt[];
  height: number;
  uid: string;
  reduce: boolean;
}

// Shared draw-in for the hero line work.
function heroDraw(reduce: boolean, delay = 0, duration = 1.5) {
  return {
    initial: reduce ? false : { pathLength: 0 },
    animate: { pathLength: 1 },
    transition: { duration, delay, ease: HERO_DRAW_EASE },
  } as const;
}

function heroFadeIn(reduce: boolean, delay: number, to = 1) {
  return {
    initial: reduce ? false : { opacity: 0 },
    animate: { opacity: to },
    transition: { duration: 0.5, delay },
  } as const;
}

function HeroPulse({ at, color }: { at: HeroPt; color: string }) {
  return (
    <>
      <motion.circle
        cx={at.x}
        cy={at.y}
        fill="none"
        style={{ stroke: color }}
        strokeWidth="1"
        initial={{ r: 3, opacity: 0.7 }}
        animate={{ r: [3, 14], opacity: [0.7, 0] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
      />
      <circle cx={at.x} cy={at.y} r="3" style={{ fill: color }} />
    </>
  );
}

// 1 · Higher highs, higher lows — a glowing trend inside its own channel.
function HeroTrendChannel({ points, uid, reduce }: HeroChartProps) {
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const lows = heroPivots(points, "low");
  const highs = heroPivots(points, "high");
  const x0 = first.x;
  const x1 = last.x + 10;
  const support =
    lows.length >= 2
      ? heroLineThrough(points[lows[0]!]!, points[lows.at(-1)!]!, x0, x1)
      : null;
  const resistance =
    highs.length >= 2
      ? heroLineThrough(points[highs[0]!]!, points[highs.at(-1)!]!, x0, x1)
      : null;
  const line = heroLinePath(points);
  const area = `${line} L ${last.x} ${first.y} L ${first.x} ${first.y} Z`;

  return (
    <g>
      <defs>
        <linearGradient id={`${uid}-area`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.32" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id={`${uid}-stroke`}
          gradientUnits="userSpaceOnUse"
          x1={first.x}
          y1="0"
          x2={last.x}
          y2="0"
        >
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.25" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="1" />
        </linearGradient>
      </defs>
      {support && resistance && (
        <motion.polygon
          points={`${support.x0},${support.y0} ${support.x1},${support.y1} ${resistance.x1},${resistance.y1} ${resistance.x0},${resistance.y0}`}
          fill="currentColor"
          {...heroFadeIn(reduce, 1.1, 0.07)}
        />
      )}
      {[support, resistance].map(
        (l, i) =>
          l && (
            <motion.path
              key={i}
              d={`M ${l.x0} ${l.y0} L ${l.x1} ${l.y1}`}
              stroke="currentColor"
              strokeOpacity="0.45"
              strokeWidth="1"
              strokeDasharray="2 4"
              fill="none"
              vectorEffect="non-scaling-stroke"
              {...heroDraw(reduce, 0.9 + i * 0.2, 0.9)}
            />
          ),
      )}
      <motion.path
        d={area}
        fill={`url(#${uid}-area)`}
        {...heroFadeIn(reduce, 0.8)}
      />
      <motion.path
        d={line}
        fill="none"
        stroke={`url(#${uid}-stroke)`}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter={`url(#${uid}-glow)`}
        vectorEffect="non-scaling-stroke"
        {...heroDraw(reduce)}
      />
      {lows.map((index, i) => (
        <motion.circle
          key={index}
          cx={points[index]!.x}
          cy={points[index]!.y}
          r="1.75"
          fill="currentColor"
          {...heroFadeIn(reduce, 0.5 + i * 0.12, 0.8)}
        />
      ))}
      <motion.g {...heroFadeIn(reduce, 1.4)}>
        <HeroPulse at={last} color="currentColor" />
      </motion.g>
    </g>
  );
}

// 2 · Accumulation then breakout — candles print one by one and break the ceiling.
function HeroBreakoutCandles({ points, uid, reduce }: HeroChartProps) {
  const range = points.slice(0, 8);
  const ceiling = Math.min(...range.map((p) => p.y));
  const spacing =
    (points[points.length - 1]!.x - points[0]!.x) / (points.length - 1);
  const bodyW = Math.max(3, Math.min(9, spacing * 0.62));
  const breakoutIndex = points.findIndex((p, i) => i >= 8 && p.y < ceiling);
  const candles = points.slice(1).map((close, k) => {
    const open = points[k]!;
    const up = close.y <= open.y;
    const top = Math.min(open.y, close.y);
    const bottom = Math.max(open.y, close.y);
    const wick = 1.5 + (bottom - top) * 0.22;
    return {
      x: (open.x + close.x) / 2,
      top,
      bottom: Math.max(bottom, top + 1.5),
      wickTop: top - wick,
      wickBottom: bottom + wick * 0.7,
      up,
      breakout: breakoutIndex > 0 && k + 1 >= breakoutIndex,
    };
  });
  const ceilingX0 = points[0]!.x - 6;
  const ceilingX1 = points[points.length - 1]!.x + 14;
  const breakout = breakoutIndex > 0 ? candles[breakoutIndex - 1] : null;

  return (
    <g>
      <motion.path
        d={`M ${ceilingX0} ${ceiling} L ${ceilingX1} ${ceiling}`}
        style={{ stroke: HERO_AMBER }}
        strokeOpacity="0.7"
        strokeWidth="1"
        strokeDasharray="3 3"
        fill="none"
        vectorEffect="non-scaling-stroke"
        {...heroDraw(reduce, 0.2, 0.8)}
      />
      <motion.path
        d={heroLinePath(points)}
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
        {...heroDraw(reduce, 0.3, 1.8)}
      />
      {candles.map((c, i) => (
        <motion.g
          key={i}
          style={{ transformBox: "fill-box", transformOrigin: "center" }}
          initial={reduce ? false : { opacity: 0, scaleY: 0 }}
          animate={{ opacity: 1, scaleY: 1 }}
          transition={{ duration: 0.28, delay: 0.25 + i * 0.1, ease: EASE_OUT }}
          filter={c.breakout ? `url(#${uid}-glow)` : undefined}
        >
          <line
            x1={c.x}
            x2={c.x}
            y1={c.wickTop}
            y2={c.wickBottom}
            style={{ stroke: c.up ? HERO_UP : HERO_DOWN }}
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
          <rect
            x={c.x - bodyW / 2}
            y={c.top}
            width={bodyW}
            height={c.bottom - c.top}
            rx="1"
            style={{ fill: c.up ? HERO_UP : HERO_DOWN }}
            fillOpacity={c.up ? (c.breakout ? 1 : 0.85) : 0.75}
          />
        </motion.g>
      ))}
      {breakout && (
        <motion.text
          x={breakout.x}
          y={ceiling - 7}
          textAnchor="middle"
          className="font-mono"
          fontSize="8"
          letterSpacing="0.14em"
          style={{ fill: HERO_AMBER }}
          {...heroFadeIn(reduce, 0.35 + breakoutIndex * 0.1)}
        >
          BREAKOUT
        </motion.text>
      )}
    </g>
  );
}

// 3 · Double bottom — the "W" lights up, then price clears the neckline.
function HeroDoubleBottom({ points, uid, reduce }: HeroChartProps) {
  const lows = heroPivots(points, "low")
    .sort((a, b) => points[b]!.y - points[a]!.y)
    .slice(0, 2)
    .sort((a, b) => a - b);
  if (lows.length < 2) return null;
  const [b1, b2] = lows as [number, number];
  const between = points.slice(b1, b2 + 1);
  const neckY = Math.min(...between.map((p) => p.y));
  const last = points[points.length - 1]!;
  const first = points[0]!;

  let cross: HeroPt | null = null;
  for (let i = b2; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    if (a.y >= neckY && b.y < neckY) {
      const t = (a.y - neckY) / (a.y - b.y || 1);
      cross = { x: a.x + (b.x - a.x) * t, y: neckY };
      break;
    }
  }
  const wShape = `${heroLinePath(between)} L ${between.at(-1)!.x} ${neckY} L ${between[0]!.x} ${neckY} Z`;

  return (
    <g>
      <defs>
        <linearGradient
          id={`${uid}-w`}
          gradientUnits="userSpaceOnUse"
          x1={first.x}
          y1="0"
          x2={last.x}
          y2="0"
        >
          <stop offset="0%" style={{ stopColor: HERO_AMBER }} />
          <stop offset="60%" style={{ stopColor: HERO_AMBER }} />
          <stop offset="100%" stopColor="currentColor" />
        </linearGradient>
      </defs>
      <motion.path
        d={wShape}
        style={{ fill: HERO_AMBER }}
        {...heroFadeIn(reduce, 1, 0.14)}
      />
      <motion.path
        d={`M ${points[b1]!.x - 8} ${neckY} L ${last.x + 12} ${neckY}`}
        style={{ stroke: HERO_AMBER }}
        strokeOpacity="0.75"
        strokeWidth="1"
        strokeDasharray="4 3"
        fill="none"
        vectorEffect="non-scaling-stroke"
        {...heroDraw(reduce, 0.9, 0.8)}
      />
      <motion.path
        d={heroLinePath(points)}
        fill="none"
        stroke={`url(#${uid}-w)`}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter={`url(#${uid}-glow)`}
        vectorEffect="non-scaling-stroke"
        {...heroDraw(reduce, 0, 1.7)}
      />
      {[b1, b2].map((index, i) => (
        <motion.g key={index} {...heroFadeIn(reduce, 0.45 + i * 0.35)}>
          <circle
            cx={points[index]!.x}
            cy={points[index]!.y}
            r="5"
            fill="none"
            style={{ stroke: HERO_AMBER }}
            strokeWidth="1"
          />
          <circle
            cx={points[index]!.x}
            cy={points[index]!.y}
            r="1.75"
            style={{ fill: HERO_AMBER }}
          />
        </motion.g>
      ))}
      {cross && (
        <motion.g
          style={{ transformBox: "fill-box", transformOrigin: "center" }}
          initial={reduce ? false : { opacity: 0, scale: 0.4 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 1.35, ease: EASE_OUT }}
        >
          <circle
            cx={cross.x}
            cy={cross.y}
            r="7"
            fill="currentColor"
            fillOpacity="0.18"
          />
          <circle cx={cross.x} cy={cross.y} r="2.5" fill="currentColor" />
        </motion.g>
      )}
      <motion.g {...heroFadeIn(reduce, 1.6)}>
        <HeroPulse at={last} color="currentColor" />
      </motion.g>
    </g>
  );
}

// 4 · Impulse, flag, continuation — the pole glows, the flag is a shaded channel.
function HeroBullFlag({ points, uid, reduce }: HeroChartProps) {
  const poleEnd = 3;
  const flagEnd = 9;
  const pole = points.slice(0, poleEnd + 1);
  const flag = points.slice(poleEnd, flagEnd + 1);
  const run = points.slice(flagEnd);
  const a = flag[0]!;
  const b = flag.at(-1)!;
  const slope = (b.y - a.y) / (b.x - a.x || 1);
  const residuals = flag.map((p) => p.y - (a.y + slope * (p.x - a.x)));
  const up = Math.min(...residuals) - 1.5;
  const down = Math.max(...residuals) + 1.5;
  const at = (x: number, off: number) => a.y + slope * (x - a.x) + off;
  const channel = `${a.x},${at(a.x, up)} ${b.x},${at(b.x, up)} ${b.x},${at(b.x, down)} ${a.x},${at(a.x, down)}`;

  return (
    <g>
      <defs>
        <marker
          id={`${uid}-arrow`}
          viewBox="0 0 10 10"
          refX="5"
          refY="5"
          markerWidth="5"
          markerHeight="5"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
        </marker>
      </defs>
      <motion.path
        d={heroLinePath(pole)}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        filter={`url(#${uid}-glow)`}
        vectorEffect="non-scaling-stroke"
        {...heroDraw(reduce, 0, 0.5)}
      />
      <motion.polygon
        points={channel}
        fill="currentColor"
        fillOpacity="0.1"
        stroke="currentColor"
        strokeOpacity="0.45"
        strokeDasharray="2 3"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
        {...heroFadeIn(reduce, 0.55)}
      />
      <motion.path
        d={heroLinePath(flag)}
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.6"
        strokeWidth="1.25"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        {...heroDraw(reduce, 0.5, 0.8)}
      />
      <motion.path
        d={heroLinePath(run)}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
        markerEnd={`url(#${uid}-arrow)`}
        filter={`url(#${uid}-glow)`}
        vectorEffect="non-scaling-stroke"
        {...heroDraw(reduce, 1.3, 0.6)}
      />
    </g>
  );
}

// 5 · Volatile recovery — heat-mapped curve, volume underneath, a comet on top.
function HeroVolatileRecovery({ points, height, uid, reduce }: HeroChartProps) {
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const d = heroSmoothPath(points);
  const span =
    Math.max(...points.map((p) => p.y)) - Math.min(...points.map((p) => p.y));
  const bars = points.slice(1).map((p, i) => {
    const prev = points[i]!;
    return {
      x: p.x,
      h:
        2 +
        (Math.abs(prev.y - p.y) / (span || 1)) * Math.min(18, height * 0.22),
      up: p.y < prev.y,
    };
  });

  return (
    <g>
      <defs>
        <linearGradient
          id={`${uid}-heat`}
          gradientUnits="userSpaceOnUse"
          x1={first.x}
          y1="0"
          x2={last.x}
          y2="0"
        >
          <stop offset="0%" style={{ stopColor: HERO_DOWN }} />
          <stop offset="50%" style={{ stopColor: HERO_AMBER }} />
          <stop offset="100%" style={{ stopColor: HERO_UP }} />
        </linearGradient>
      </defs>
      {bars.map((bar, i) => (
        <motion.rect
          key={i}
          x={bar.x - 1.5}
          width="3"
          rx="1"
          y={height - bar.h}
          height={bar.h}
          style={{
            fill: bar.up ? HERO_UP : HERO_DOWN,
            transformBox: "fill-box",
            transformOrigin: "bottom",
          }}
          initial={reduce ? false : { scaleY: 0, opacity: 0 }}
          animate={{ scaleY: 1, opacity: 0.4 }}
          transition={{ duration: 0.3, delay: 0.2 + i * 0.07 }}
        />
      ))}
      <motion.path
        d={d}
        fill="none"
        stroke={`url(#${uid}-heat)`}
        strokeWidth="2"
        strokeLinecap="round"
        filter={`url(#${uid}-glow)`}
        vectorEffect="non-scaling-stroke"
        {...heroDraw(reduce, 0, 1.8)}
      />
      {!reduce &&
        [0, 1, 2, 3, 4].map((k) => (
          <motion.circle
            key={k}
            r={3 - k * 0.45}
            style={{ fill: k === 0 ? "hsl(var(--foreground))" : HERO_UP }}
            initial={{ cx: first.x, cy: first.y, opacity: 0 }}
            animate={{
              cx: points.map((p) => p.x),
              cy: points.map((p) => p.y),
              opacity: [0, 1 - k * 0.18, 1 - k * 0.18, 0],
            }}
            transition={{
              duration: 1.8,
              delay: k * 0.06,
              ease: "linear",
              opacity: {
                duration: 1.8,
                delay: k * 0.06,
                times: [0, 0.1, 0.9, 1],
              },
            }}
          />
        ))}
      <motion.g {...heroFadeIn(reduce, 1.8)}>
        <HeroPulse at={last} color={HERO_UP} />
      </motion.g>
    </g>
  );
}

const HERO_CHART_DESIGNS = [
  HeroTrendChannel,
  HeroBreakoutCandles,
  HeroDoubleBottom,
  HeroBullFlag,
  HeroVolatileRecovery,
] as const;

function HeroTitle() {
  const reduce = useReducedMotion() ?? false;
  const uid = useId().replace(/:/g, "");
  const titleRef = useRef<HTMLHeadingElement>(null);
  const upperDotRef = useRef<HTMLSpanElement>(null);
  const lowerDotRef = useRef<HTMLSpanElement>(null);
  const [chartIndex, setChartIndex] = useState(0);
  const [geometry, setGeometry] = useState({
    width: 0,
    height: 0,
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
  });

  useEffect(() => {
    const title = titleRef.current;
    const upperDot = upperDotRef.current;
    const lowerDot = lowerDotRef.current;
    if (!title || !upperDot || !lowerDot) return;
    let active = true;

    const measure = () => {
      if (!active) return;
      const titleBox = title.getBoundingClientRect();
      const upperBox = upperDot.getBoundingClientRect();
      const lowerBox = lowerDot.getBoundingClientRect();

      setGeometry({
        width: titleBox.width,
        height: titleBox.height,
        startX: lowerBox.left + lowerBox.width / 2 - titleBox.left,
        startY: lowerBox.top + lowerBox.height / 2 - titleBox.top,
        endX: upperBox.left + upperBox.width / 2 - titleBox.left,
        endY: upperBox.top + upperBox.height / 2 - titleBox.top,
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(title);
    void document.fonts?.ready.then(measure);
    window.addEventListener("resize", measure);
    return () => {
      active = false;
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    if (reduce) return;
    const timeout = window.setTimeout(() => {
      setChartIndex((current) => (current + 1) % HERO_CHART_DESIGNS.length);
    }, HERO_CHART_CYCLE * 1000);
    return () => window.clearTimeout(timeout);
  }, [chartIndex, reduce]);

  const index = reduce ? 0 : chartIndex;
  const points = (HERO_TITLE_CHARTS[index] ?? HERO_TITLE_CHARTS[0]!).map(
    ([x, y]) => ({
      x: geometry.startX + (geometry.endX - geometry.startX) * x,
      y: geometry.endY + (geometry.startY - geometry.endY) * y,
    }),
  );
  const Design = HERO_CHART_DESIGNS[index] ?? HeroTrendChannel;
  const chartIsVisible =
    geometry.width > 0 &&
    geometry.height > 0 &&
    geometry.endX - geometry.startX > 44 &&
    geometry.startY - geometry.endY > 20;

  return (
    <h1
      ref={titleRef}
      className="relative font-heading text-[1.5rem] leading-[0.98] tracking-[-0.04em] text-foreground sm:text-[2.625rem] lg:text-[3.125rem]"
    >
      {chartIsVisible && (
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0 h-full w-full overflow-visible text-primary"
          viewBox={`0 0 ${geometry.width} ${geometry.height}`}
          preserveAspectRatio="none"
        >
          <motion.g
            key={index}
            initial={reduce ? false : { opacity: 0 }}
            animate={reduce ? { opacity: 0.75 } : { opacity: [0, 1, 1, 0] }}
            transition={
              reduce
                ? { duration: 0 }
                : {
                    duration: HERO_CHART_CYCLE,
                    times: [0, 0.04, 0.88, 1],
                    ease: "linear",
                  }
            }
          >
            <defs>
              <filter
                id={`${uid}-${index}-glow`}
                x="-50%"
                y="-50%"
                width="200%"
                height="200%"
              >
                <feGaussianBlur stdDeviation="2.4" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <Design
              points={points}
              height={geometry.height}
              uid={`${uid}-${index}`}
              reduce={reduce}
            />
          </motion.g>
        </svg>
      )}

      {/* Line 1 runs wider than line 2 so its dot sits well right of line 2's:
          the chart between them needs a 44px+ run (Cal Sans: 50px phone,
          76px tablet, 90px desktop). Neither line wraps: a wrapped line 1 puts
          its dot left of line 2's and hides the chart. On desktop the lines
          may run over the sphere's left edge (the copy sits above it). */}
      <span className="relative z-10 block whitespace-nowrap text-[1.15em]">
        450+ stocks, 30+ countries
        <span
          ref={upperDotRef}
          aria-hidden="true"
          className="ml-[0.08em] inline-block size-[0.16em] rounded-full bg-current align-[0.08em]"
        />
      </span>
      <span className="relative z-10 mt-1 block whitespace-nowrap text-primary">
        buy/trade with stablecoins
        <span
          ref={lowerDotRef}
          aria-hidden="true"
          className="ml-[0.08em] inline-block size-[0.16em] rounded-full bg-current align-[0.08em]"
        />
        <span className="sr-only">.</span>
      </span>
    </h1>
  );
}

// The wallet onboarding orbit's token set (wallet/lib/presentation/onboarding_kit/token_orbit.dart),
// plus more stocks, ETFs and tokens so the oversized web sphere reads dense.
const ORBIT_SYMBOLS = [
  "BTC",
  "AAPL",
  "ETH",
  "SOL",
  "TSLA",
  "USDT",
  "USDC",
  "NVDA",
  "BNB",
  "XRP",
  "MSFT",
  "ADA",
  "DOGE",
  "AMZN",
  "AVAX",
  "DOT",
  "GOOGL",
  "MATIC",
  "LINK",
  "META",
  "UNI",
  "LTC",
  "SPY",
  "ATOM",
  "TRX",
  "COIN",
  "XLM",
  "NEAR",
  "QQQ",
  "APT",
  "ARB",
  "NFLX",
  "OP",
  "FIL",
  "MSTR",
  "ICP",
  "HBAR",
  "GLD",
  "ETC",
  "AAVE",
  "AMD",
  "MKR",
  "SHIB",
  "PLTR",
  "PEPE",
  "INJ",
  "HOOD",
  "ALGO",
  "GRT",
  "UBER",
  "SAND",
  "CRV",
  "SHOP",
  "LDO",
  "DAI",
  "PYPL",
  "TON",
  "SUI",
  "ABNB",
  "WLD",
  "HYPE",
  "ENA",
  "ONDO",
  "EIGEN",
  "ZRO",
  "BCH",
  "XMR",
  "VET",
  "FET",
  "STX",
  "IMX",
  "RUNE",
  "GALA",
  "AXS",
  "APE",
  "INTC",
  "ARM",
  "SMCI",
  "BABA",
  "RBLX",
  "SQ",
  "NIO",
  "RIVN",
  "DKNG",
  "GME",
  "IWM",
  "SLV",
  "ARKK",
  "USO",
  "GDX",
  "SNX",
  "COMP",
  "YFI",
  "SUSHI",
  "MANA",
  "CHZ",
  "ENJ",
  "BAT",
  "THETA",
  "EGLD",
  "FLOW",
  "NEO",
  "QNT",
  "CAKE",
  "KSM",
  "ZEC",
  "DASH",
  "XTZ",
  "EOS",
  "KAVA",
  "CRO",
  "ZK",
  "W",
  "PURR",
  "NOT",
] as const;

const ORBIT_ICON = 44;
const ORBIT_BASE_SPEED = 0.22; // rad/s, matches the wallet orbit
const ORBIT_BASE_TILT = 0.38;
const ORBIT_FOCAL = 3.2;

function fibonacciSphere(n: number) {
  const golden = Math.PI * (3 - Math.sqrt(5));
  return Array.from({ length: n }, (_, i) => {
    const y = 1 - (i / (n - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    const theta = golden * i;
    return { x: Math.cos(theta) * r, y, z: Math.sin(theta) * r };
  });
}

// Port of the wallet's onboarding token sphere: a Fibonacci-distributed globe
// of tradable assets with perspective, depth fade, and drag-to-spin inertia.
// Painted imperatively per frame so 100+ icons never trigger React renders.
function TokenOrbit({ className }: { className?: string }) {
  const reduce = useReducedMotion();
  const stageRef = useRef<HTMLDivElement>(null);
  const iconRefs = useRef<(HTMLDivElement | null)[]>([]);
  const points = useMemo(() => fibonacciSphere(ORBIT_SYMBOLS.length), []);
  const motionState = useRef({
    angle: 0,
    tilt: ORBIT_BASE_TILT,
    tiltTarget: ORBIT_BASE_TILT,
    velocity: ORBIT_BASE_SPEED,
    dragging: false,
    lastX: 0,
    lastY: 0,
    lastT: 0,
  });

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const s = motionState.current;
    let size = stage.clientWidth;
    let visible = true;
    let frame = 0;
    let last = performance.now();

    const paint = () => {
      const radius = size * 0.38;
      const c = size / 2;
      const cosA = Math.cos(s.angle);
      const sinA = Math.sin(s.angle);
      const cosT = Math.cos(s.tilt);
      const sinT = Math.sin(s.tilt);
      const fit = Math.min(Math.max(size / 520, 0.6), 1.6);
      for (let i = 0; i < points.length; i++) {
        const el = iconRefs.current[i];
        const p = points[i];
        if (!el || !p) continue;
        const x1 = p.x * cosA + p.z * sinA;
        const z1 = -p.x * sinA + p.z * cosA;
        const y2 = p.y * cosT - z1 * sinT;
        const z2 = p.y * sinT + z1 * cosT;
        const persp = ORBIT_FOCAL / (ORBIT_FOCAL - z2);
        const depth = (z2 + 1) / 2;
        const scale = (0.42 + 0.68 * depth) * fit;
        el.style.transform = `translate3d(${(c + x1 * radius * persp - ORBIT_ICON / 2).toFixed(1)}px, ${(c + y2 * radius * persp - ORBIT_ICON / 2).toFixed(1)}px, 0) scale(${scale.toFixed(3)})`;
        el.style.opacity = (0.06 + 0.94 * depth * depth).toFixed(3);
        el.style.zIndex = String(Math.round(depth * 1000));
      }
    };

    const tick = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      if (visible) {
        if (!s.dragging) {
          s.velocity +=
            (ORBIT_BASE_SPEED - s.velocity) * (1 - Math.exp(-dt * 1.4));
          s.tiltTarget +=
            (ORBIT_BASE_TILT - s.tiltTarget) * (1 - Math.exp(-dt * 0.8));
          s.angle = (s.angle + dt * s.velocity) % (Math.PI * 2);
        }
        s.tilt += (s.tiltTarget - s.tilt) * (1 - Math.exp(-dt * 4));
        paint();
      }
      frame = requestAnimationFrame(tick);
    };

    const resize = new ResizeObserver(() => {
      size = stage.clientWidth;
      paint();
    });
    resize.observe(stage);
    const io = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
    });
    io.observe(stage);

    paint();
    if (!reduce) frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      io.disconnect();
    };
  }, [points, reduce]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (reduce) return;
    const s = motionState.current;
    s.dragging = true;
    s.lastX = e.clientX;
    s.lastY = e.clientY;
    s.lastT = performance.now();
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const s = motionState.current;
    if (!s.dragging) return;
    const now = performance.now();
    const dx = e.clientX - s.lastX;
    const dy = e.clientY - s.lastY;
    const dt = Math.max((now - s.lastT) / 1000, 0.008);
    const turn = dx * 0.006;
    s.angle = (s.angle + turn) % (Math.PI * 2);
    s.velocity = Math.max(-6, Math.min(6, turn / dt));
    s.tiltTarget = Math.max(-0.3, Math.min(1.1, s.tiltTarget + dy * 0.004));
    s.lastX = e.clientX;
    s.lastY = e.clientY;
    s.lastT = now;
  };
  const onPointerUp = () => {
    motionState.current.dragging = false;
  };

  return (
    <div
      className={cn("relative aspect-square w-full select-none", className)}
      aria-hidden="true"
    >
      {/* Atmosphere: core glow, equator + meridian rings */}
      <div className="pointer-events-none absolute inset-[8%] rounded-full bg-[radial-gradient(circle_at_50%_45%,hsl(var(--glow-a)/0.30)_0%,hsl(var(--glow-a)/0.10)_40%,transparent_70%)] blur-2xl" />
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full text-primary"
        viewBox="0 0 100 100"
      >
        <circle
          cx="50"
          cy="50"
          r="38"
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.14"
          strokeWidth="0.2"
        />
        <ellipse
          cx="50"
          cy="50"
          rx="38"
          ry="14"
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.12"
          strokeWidth="0.2"
          strokeDasharray="0.6 1.4"
          transform="rotate(-8 50 50)"
        />
        <ellipse
          cx="50"
          cy="50"
          rx="14"
          ry="38"
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.08"
          strokeWidth="0.2"
          strokeDasharray="0.6 1.4"
          transform="rotate(-8 50 50)"
        />
      </svg>
      <div
        ref={stageRef}
        className={cn(
          "absolute inset-0 touch-pan-y",
          !reduce && "cursor-grab active:cursor-grabbing",
        )}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <span className="pointer-events-none absolute left-1/2 top-1/2 z-[500] -translate-x-1/2 -translate-y-1/2 whitespace-nowrap font-heading text-3xl tracking-tight text-foreground [text-shadow:0_0_32px_hsl(var(--glow-a)/0.6)] sm:text-4xl lg:text-5xl">
          Fexr
          <span className="-ml-[0.04em] text-[0.6em] tracking-[-0.06em]">
            .io
          </span>
        </span>
        {ORBIT_SYMBOLS.map((symbol, i) => (
          <div
            key={symbol}
            ref={(el) => {
              iconRefs.current[i] = el;
            }}
            className="pointer-events-none absolute left-0 top-0 origin-center rounded-full opacity-0 shadow-[0_6px_18px_-6px_rgba(0,0,0,0.45)] will-change-transform"
            style={{ width: ORBIT_ICON, height: ORBIT_ICON }}
          >
            <AssetIcon symbol={symbol} size={ORBIT_ICON} />
          </div>
        ))}
      </div>
    </div>
  );
}

const HERO_ORDERS = [
  {
    ref: "ORD-48217",
    source: "Your instruction",
    sourceDetail: "“Long ETH, $120, 2× leverage”",
    asset: "ETH-PERP",
    icon: "ETH",
    side: "Long",
    notional: 120,
    leverage: 2,
    concentration: 18,
    priceAge: 4,
    funding: 0.011,
  },
  {
    ref: "ORD-48218",
    source: "Fexr intelligence",
    sourceDetail: "TSLA momentum signal · 91% confidence",
    asset: "TSLA-PERP",
    icon: "TSLA",
    side: "Long",
    notional: 80,
    leverage: 1,
    concentration: 12,
    priceAge: 2,
    funding: 0.006,
  },
  {
    ref: "ORD-48219",
    source: "Strategy agent",
    sourceDetail: "Gold breakout strategy · backtest attached",
    asset: "GOLD-PERP",
    icon: "XAU",
    side: "Long",
    notional: 150,
    leverage: 2,
    concentration: 22,
    priceAge: 6,
    funding: 0.009,
  },
] as const;

// The limits a user sets once; every order is measured against them.
const HERO_POLICY = {
  maxOrder: 500,
  maxLeverage: 3,
  maxConcentration: 25,
  maxPriceAge: 30,
  maxFunding: 0.05,
};

function heroPermissionChecks(order: (typeof HERO_ORDERS)[number]) {
  return [
    {
      label: "Order size",
      limit: `$${HERO_POLICY.maxOrder} max`,
      value: `$${order.notional}`,
      used: order.notional / HERO_POLICY.maxOrder,
    },
    {
      label: "Leverage",
      limit: `${HERO_POLICY.maxLeverage}× max`,
      value: `${order.leverage}×`,
      used: order.leverage / HERO_POLICY.maxLeverage,
    },
    {
      label: "Share of capital",
      limit: `${HERO_POLICY.maxConcentration}% max`,
      value: `${order.concentration}%`,
      used: order.concentration / HERO_POLICY.maxConcentration,
    },
    {
      label: "Price freshness",
      limit: `${HERO_POLICY.maxPriceAge}s max`,
      value: `${order.priceAge}s`,
      used: order.priceAge / HERO_POLICY.maxPriceAge,
    },
    {
      label: "Funding rate",
      limit: `${HERO_POLICY.maxFunding}%/h max`,
      value: `${order.funding.toFixed(3)}%/h`,
      used: order.funding / HERO_POLICY.maxFunding,
    },
    {
      label: "Asset allowed",
      limit: "Your list",
      value: order.icon === "XAU" ? "GOLD" : order.icon,
      used: null,
    },
  ];
}

function PermissionBoundaryHero() {
  const reduce = useReducedMotion();
  const [orderIndex, setOrderIndex] = useState(0);
  const [rotationStopped, setRotationStopped] = useState(false);
  const order = HERO_ORDERS[orderIndex] ?? HERO_ORDERS[0];
  const checks = heroPermissionChecks(order);

  useEffect(() => {
    if (reduce || rotationStopped) return;
    const interval = window.setInterval(() => {
      setOrderIndex((current) => (current + 1) % HERO_ORDERS.length);
    }, 5200);
    return () => window.clearInterval(interval);
  }, [reduce, rotationStopped]);

  const stopRotation = useCallback(() => setRotationStopped(true), []);

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.5, ease: EASE_OUT }}
      className="relative overflow-hidden rounded-xl border border-border bg-background font-small shadow-[0_24px_60px_-30px_rgba(0,0,0,0.45)]"
      onPointerDownCapture={stopRotation}
      onKeyDownCapture={stopRotation}
      onWheelCapture={stopRotation}
    >
      {/* Title bar */}
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <ShieldCheckGlyph />
          <div>
            <p className="text-sm font-semibold text-foreground">
              Pre-trade check
            </p>
            <p className="font-number text-xs text-muted-foreground">
              {order.ref} · example
            </p>
          </div>
        </div>
        <div
          role="tablist"
          aria-label="Example orders"
          className="flex items-center gap-1"
        >
          {HERO_ORDERS.map((item, index) => (
            <button
              key={item.ref}
              type="button"
              role="tab"
              aria-selected={index === orderIndex}
              aria-label={`Show ${item.asset} example`}
              onClick={() => setOrderIndex(index)}
              className={cn(
                "h-1.5 rounded-full transition-all",
                index === orderIndex
                  ? "w-5 bg-primary"
                  : "w-1.5 bg-foreground/15 hover:bg-foreground/30",
              )}
            />
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={order.ref}
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduce ? undefined : { opacity: 0 }}
          transition={{ duration: 0.2 }}
          aria-live="polite"
        >
          {/* Order summary */}
          <div className="grid gap-4 border-b border-border px-5 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
            <div className="flex min-w-0 items-center gap-3">
              <AssetIcon symbol={order.icon} size={36} />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-number text-lg font-semibold tracking-tight text-foreground">
                    {order.asset}
                  </p>
                  <span className="rounded-md bg-success/10 px-1.5 py-0.5 text-xs font-medium text-success">
                    {order.side}
                  </span>
                </div>
                <p className="truncate text-xs text-muted-foreground">
                  {order.source} — {order.sourceDetail}
                </p>
              </div>
            </div>
            <dl className="grid grid-cols-3 gap-5 text-left sm:text-right">
              {[
                { label: "Size", value: `$${order.notional}` },
                { label: "Leverage", value: `${order.leverage}×` },
                { label: "Type", value: "Market" },
              ].map((item) => (
                <div key={item.label}>
                  <dt className="text-xs text-muted-foreground">
                    {item.label}
                  </dt>
                  <dd className="font-number text-sm font-semibold text-foreground">
                    {item.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Checks against the user's limits */}
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="px-5 py-2 font-medium">Check</th>
                <th className="hidden px-3 py-2 font-medium sm:table-cell">
                  Your limit
                </th>
                <th className="px-3 py-2 font-medium">This order</th>
                <th className="hidden w-[22%] px-3 py-2 font-medium sm:table-cell">
                  Limit used
                </th>
                <th className="px-5 py-2 text-right font-medium">Result</th>
              </tr>
            </thead>
            <tbody>
              {checks.map((check, index) => (
                <motion.tr
                  key={check.label}
                  initial={reduce ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.25, delay: 0.1 + index * 0.08 }}
                  className="border-b border-border/60 last:border-b-0"
                >
                  <td className="px-5 py-2.5 text-foreground">{check.label}</td>
                  <td className="hidden px-3 py-2.5 font-number text-muted-foreground sm:table-cell">
                    {check.limit}
                  </td>
                  <td className="px-3 py-2.5 font-number font-medium text-foreground">
                    {check.value}
                  </td>
                  <td className="hidden px-3 py-2.5 sm:table-cell">
                    {check.used === null ? (
                      <span className="text-muted-foreground">On list</span>
                    ) : (
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-foreground/[0.07]">
                        <motion.div
                          className="h-full rounded-full bg-primary/70"
                          initial={reduce ? false : { width: 0 }}
                          animate={{
                            width: `${Math.min(check.used, 1) * 100}%`,
                          }}
                          transition={{
                            duration: 0.5,
                            delay: 0.15 + index * 0.08,
                            ease: EASE_OUT,
                          }}
                        />
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-right">
                    <span className="inline-flex items-center gap-1 font-medium text-success">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                      Pass
                    </span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </AnimatePresence>

      {/* Outcome */}
      <div className="flex flex-col gap-3 border-t border-border bg-muted/30 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between">
        <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
          <div className="flex gap-1.5">
            <dt className="text-muted-foreground">Funds held in</dt>
            <dd className="font-medium text-foreground">Your wallet</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-muted-foreground">Can withdraw</dt>
            <dd className="font-medium text-foreground">Only you</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-muted-foreground">Routed to</dt>
            <dd className="font-medium text-foreground">Hyperliquid</dd>
          </div>
        </dl>
        <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-success/10 px-2.5 py-1 text-xs font-semibold text-success sm:self-auto">
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          {checks.length}/{checks.length} passed · sent
        </span>
      </div>
    </motion.div>
  );
}

function ShieldCheckGlyph() {
  return (
    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    </span>
  );
}

const EXECUTION_STACK = [
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
        <polyline points="16 7 22 7 22 13" />
      </svg>
    ),
    label: "Discover",
    value: "Qualified markets",
    detail:
      "Scan a quality-screened selection of crypto, tokenised equities, and commodities from a single board.",
    color: "hsl(var(--chart-5))",
  },
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
      </svg>
    ),
    label: "Build conviction",
    value: "Signals + context",
    detail:
      "Combine price action, activity, technical signals, and community intelligence before acting.",
    color: "hsl(var(--engagement-lavender))",
  },
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4l3 3" />
      </svg>
    ),
    label: "Control risk",
    value: "Your limits",
    detail:
      "Set a spending cap and keep a revocable trading permission between your wallet and the agent.",
    color: "hsl(var(--chart-3))",
  },
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M5 12h14" />
        <path d="m13 6 6 6-6 6" />
        <path d="M5 6v12" />
      </svg>
    ),
    label: "Execute",
    value: "From your wallet",
    detail:
      "Move from a signal to a capped strategy without surrendering withdrawal control.",
    color: "hsl(var(--chart-2))",
  },
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 3v18h18" />
        <path d="m7 15 4-4 3 3 5-7" />
      </svg>
    ),
    label: "Monitor",
    value: "Live exposure",
    detail:
      "Track positions, PnL, strategy state, and daily risk utilisation in the terminal.",
    color: "hsl(var(--chart-1))",
  },
];

const VAULT_CASE_RULES = [
  { rule: "Members", setting: "6 partner wallets, by invitation" },
  { rule: "Mandate", setting: "GOLD, SPY, QQQ and BTC only" },
  { rule: "Position limit", setting: "10% of capital per position" },
  { rule: "Leverage", setting: "3× maximum" },
  { rule: "Daily loss stop", setting: "Trading pauses at −2% on the day" },
  { rule: "Approvals", setting: "Over $50k needs 4 of 6 partner votes" },
  { rule: "Withdrawals", setting: "Only to members' own wallets" },
];

const VAULT_CASE_PARTNERS = ["A", "B", "C", "D", "E", "F"];

const VAULT_CASE_STEPS = [
  "The group's agent spots a gold breakout and proposes a $40k long.",
  "The vault contract checks it against the mandate, position limit, leverage and today's loss.",
  "It is under $50k, so it is sent right away. A $90k order would wait for a partner vote.",
  "The order, every check and the result are recorded on-chain for all six partners.",
];

const VAULT_CASE_OUTCOMES = [
  {
    title: "One shared view",
    detail: "Research, positions and decisions in one place, not six chats.",
  },
  {
    title: "Works around the clock",
    detail: "The agent trades 24/7, but only inside the mandate.",
  },
  {
    title: "No single point of failure",
    detail: "No partner acting alone, and not Fexr, can move funds out.",
  },
  {
    title: "Audit-ready by default",
    detail: "A complete, verifiable record for reporting and tax.",
  },
];

// Landing analytics: one delegated click listener names every call to action
// by where it goes, and each section reports once when it scrolls into view.
function landingCta(href: string): string | null {
  try {
    const url = new URL(href, window.location.origin);
    if (
      /(^|\.)fexr\.io$/.test(url.hostname) &&
      url.pathname.startsWith("/plans")
    )
      return "plans";
    if (url.origin !== window.location.origin) return null;
    const first = url.pathname.split("/").filter(Boolean);
    if (first[0] === "dashboard") return first[1] ?? "dashboard";
    return first[0] ?? null;
  } catch {
    return null;
  }
}

function useLandingAnalytics() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const el = (e.target as Element | null)?.closest?.("a[href], button");
      if (!el || !el.closest("[data-landing]")) return;
      const href = el.getAttribute("href");
      const cta =
        (el as HTMLElement).dataset.cta ?? (href ? landingCta(href) : null);
      if (!cta) return;
      const section = el.closest("section")?.id || "page";
      trackEvent("landing.cta_clicked", { cta, section });
      if (cta === "plans")
        trackEvent("landing.pricing_viewed", { plan: "all" });
    };
    document.addEventListener("click", onClick, { capture: true });

    const seen = new Set<string>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = (entry.target as HTMLElement).id;
          if (!entry.isIntersecting || !id || seen.has(id)) continue;
          seen.add(id);
          trackEvent("landing.section_viewed", { section: id });
          io.unobserve(entry.target);
        }
      },
      { threshold: 0.35 },
    );
    document
      .querySelectorAll("[data-landing] section[id]")
      .forEach((el) => io.observe(el));
    return () => {
      document.removeEventListener("click", onClick, { capture: true });
      io.disconnect();
    };
  }, []);
}

export function ConsumerHero() {
  const { status } = useSession();
  const isAuthed = status === "authenticated";
  useLandingAnalytics();

  return (
    <div data-landing className="contents">
      <ReferralInviteBanner isAuthed={isAuthed} />

      {/* ─── HERO — login-shell type and CTA, token sphere on the right ─── */}
      <section
        id="hero"
        className="relative overflow-hidden pb-4 pt-10 sm:pt-14 lg:flex lg:min-h-[min(44rem,calc(100svh-4rem))] lg:items-center lg:pb-10"
      >
        <div className="container relative z-10 max-w-6xl">
          <div className="flex flex-col items-center text-center lg:max-w-[min(36rem,46%)] lg:items-start lg:text-left">
            <div className="inline-block text-left">
              <HeroTitle />
            </div>
            <p className="mt-6 max-w-xl font-small text-base leading-relaxed text-muted-foreground sm:text-lg">
              Apple, Nvidia, gold ETFs and pre-IPO tokens, alongside the crypto
              you already hold. Pay with USDC or USDT, keep everything in your
              own wallet, or let an agent trade within limits you set.
            </p>
            <div className="mt-8 w-full max-w-[26.25rem] text-center lg:max-w-[22.5rem] lg:text-left">
              <PrimaryCta isAuthed={isAuthed} />
              {!isAuthed && (
                <p className="mt-4 font-small text-xs text-muted-foreground">
                  Already have an account?{" "}
                  <Link
                    href="/login"
                    className="font-medium text-foreground underline underline-offset-4 transition-colors hover:text-primary"
                  >
                    Log in
                  </Link>
                </p>
              )}
            </div>
            <a
              href="#markets"
              className="mt-10 font-small text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Browse live markets — no account needed ↓
            </a>
          </div>
        </div>

        {/* Oversized sphere bleeding off the right edge: ~70% of the ring in view, centre label kept on screen.
            The sphere is taller than the hero, so the fade sits on the hero box itself: icons and glow
            dissolve before the section edge instead of being sliced off by it. */}
        <div className="lg:absolute lg:inset-0 lg:overflow-hidden lg:[mask-image:linear-gradient(to_bottom,transparent,black_16%,black_62%,transparent)]">
          <TokenOrbit className="mx-auto mt-6 max-w-[21.25rem] sm:max-w-[27.5rem] lg:absolute lg:left-[calc(100%_-_var(--orb)_*_0.667)] lg:top-1/2 lg:mt-0 lg:w-[var(--orb)] lg:max-w-none lg:-translate-y-1/2 lg:[--orb:clamp(48rem,82vw,80rem)]" />
        </div>
      </section>

      {/* ─── FACTS — carries the hero's scale into the first section ─── */}
      <div className="container max-w-6xl">
        <dl className="grid grid-cols-2 gap-y-8 border-y border-border/60 py-8 font-small sm:grid-cols-4 sm:py-10">
          {LANDING_FACTS.map((fact) => (
            <div
              key={fact.label}
              className="flex flex-col-reverse items-center text-center sm:items-start sm:border-l sm:border-border/60 sm:pl-6 sm:text-left sm:first:border-l-0 sm:first:pl-0"
            >
              <dt className="mt-2 text-xs text-muted-foreground">
                {fact.label}
              </dt>
              <dd className="font-figure text-3xl leading-none tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                {fact.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      {/* ─── LIVE MARKETS ─── */}
      <section id="markets" className={cn(LANDING_SECTION, "before:hidden")}>
        <SectionIntro
          lead="Every tokenized stock,"
          accent="beside the crypto it trades"
          dot={false}
          body="Fexr tracks every tokenized stock, ETF and pre-IPO token on-chain, next to the crypto it trades. Buy the ones Fexr Wallet supports with USDC or USDT, or let an agent trade them inside limits you set."
        >
          <div className="mt-6 flex flex-wrap gap-x-10 gap-y-6">
            {[
              {
                value: "1000+",
                label: "stocks, ETFs and pre-IPO tokens tracked",
              },
              { value: "450+", label: "bought with USDC or USDT" },
              { value: "30+", label: "countries" },
            ].map((fig) => (
              <div key={fig.value}>
                <p className="font-figure text-4xl leading-none tracking-tight text-foreground sm:text-5xl">
                  {fig.value}
                </p>
                <p className="mt-2 max-w-44 text-xs text-muted-foreground">
                  {fig.label}
                </p>
              </div>
            ))}
          </div>
        </SectionIntro>
        <div className="container max-w-6xl">
          <MarketsBoard />
        </div>
      </section>

      {/* ─── WATCH → ASK → ACT — the data Fexr reads, Ask over it, agents act ─── */}
      <section id="intelligence" className={LANDING_SECTION}>
        <SectionIntro
          flip
          lead="Fexr reads the markets"
          accent="every second"
          body="Prices stream in tick by tick, and every stop-loss and take-profit is checked on each one. Funding, top-trader positions, large transfers, unlocks, yields and votes are read every hour. Ask, and the answer comes from those readings, not from what a model memorised."
        />
        {/* Static glow behind the panels so the section reads as a lit surface. */}
        <div className="relative isolate">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-[8%] top-1/2 -z-10 h-4/5 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,hsl(var(--glow-a)/0.22)_0%,hsl(var(--glow-a)/0.06)_45%,transparent_72%)] blur-3xl"
          />
          <MarketDataShowcase />
        </div>
      </section>

      {/* ─── VAULTS — invite-only, explained through a worked case ─── */}
      <section id="vaults" className={LANDING_SECTION}>
        <SectionIntro
          lead="Invite-only vaults"
          accent="for groups that invest together"
          body="A vault holds a group's capital under rules written into a smart contract: who is a member, what can be traded, how much risk is allowed, and who must approve. Agents trade inside those rules — nobody, including Fexr, can move funds outside them."
        >
          <span className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-border bg-background/60 px-3 py-1 text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            Vaults are currently invite-only
          </span>
          <p className="mt-3 text-xs text-muted-foreground">
            Creating a vault, adding members and distributing rewards are
            available on the Fexr Desk plan.{" "}
            <a
              href={PLANS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground underline-offset-4 hover:underline"
            >
              fexr.io/plans
            </a>
          </p>
        </SectionIntro>

        <div className="container max-w-6xl">
          <AnimateOnScroll animation="fade-blur">
            <article className="overflow-hidden rounded-xl border border-border bg-background font-small">
              <header className="grid gap-6 border-b border-border px-5 py-6 sm:px-7 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                <div>
                  <p className="text-xs text-muted-foreground">
                    Case study · illustrative example
                  </p>
                  <h3 className="mt-1.5 font-heading text-xl tracking-tight sm:text-2xl">
                    How a six-partner investment group runs $1.2M through one
                    vault
                  </h3>
                  <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
                    The partners wanted an AI agent to run a gold and equities
                    rotation. None of them wanted to hand over custody, and none
                    wanted one person able to act alone. A vault gave them both.
                  </p>
                </div>
                <div className="flex flex-wrap items-end gap-x-6 gap-y-3 lg:flex-col lg:items-end">
                  <p className="font-figure text-5xl leading-none tracking-tight text-foreground sm:text-6xl">
                    $1.2M
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex -space-x-2" aria-hidden="true">
                      {VAULT_CASE_PARTNERS.map((partner) => (
                        <span
                          key={partner}
                          className="flex size-7 items-center justify-center rounded-full border-2 border-background bg-muted text-xs font-medium text-muted-foreground"
                        >
                          {partner}
                        </span>
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      6 partners, one vault
                    </span>
                  </div>
                </div>
              </header>

              <div className="grid lg:grid-cols-2">
                <div className="border-b border-border px-5 py-5 sm:px-7 lg:border-b-0 lg:border-r">
                  <p className="text-sm font-semibold text-foreground">
                    The rules they wrote into the vault
                  </p>
                  <dl className="mt-3 divide-y divide-border/60">
                    {VAULT_CASE_RULES.map((item) => (
                      <div
                        key={item.rule}
                        className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-3 py-2.5 text-sm"
                      >
                        <dt className="text-muted-foreground">{item.rule}</dt>
                        <dd className="font-medium text-foreground">
                          {item.setting}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>

                <div className="px-5 py-5 sm:px-7">
                  <p className="text-sm font-semibold text-foreground">
                    What happens when the agent wants to trade
                  </p>
                  <ol className="mt-4 space-y-4">
                    {VAULT_CASE_STEPS.map((step, index) => (
                      <li key={step} className="flex gap-3 text-sm">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border font-number text-xs font-semibold text-foreground">
                          {index + 1}
                        </span>
                        <p className="pt-0.5 leading-relaxed text-muted-foreground">
                          {step}
                        </p>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>

              <div className="grid gap-px border-t border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
                {VAULT_CASE_OUTCOMES.map((item) => (
                  <div
                    key={item.title}
                    className="bg-muted/30 px-5 py-4 sm:px-7"
                  >
                    <p className="text-sm font-semibold text-foreground">
                      {item.title}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {item.detail}
                    </p>
                  </div>
                ))}
              </div>
            </article>
          </AnimateOnScroll>

          <div className="mt-6 flex flex-col items-center gap-2 text-center font-small">
            {/* Vaults are joined from a vault's own invite link; there is no
                request queue. An account covers everything else. */}
            <Link
              href="/register"
              className="inline-flex h-10 items-center justify-center rounded-full border border-primary/40 bg-primary/5 px-5 text-sm font-medium text-primary transition-colors hover:border-primary/70 hover:bg-primary/10"
            >
              Create a free account
            </Link>
            <a
              href="https://fexr.io/plans"
              target="_blank"
              rel="noopener"
              className="text-xs text-muted-foreground transition-colors hover:text-primary"
            >
              Running a fund or a team? See Fexr Desk →
            </a>
          </div>
        </div>
      </section>

      {/* ─── SAFEGUARDS — the live check panel beside the path an order takes ─── */}
      <section id="execution" className={LANDING_SECTION}>
        <SectionIntro
          flip
          lead="Every order is checked."
          accent="Fexr can never withdraw"
          body="You set the limits once. Before any order reaches Hyperliquid — whether you placed it or an agent did — it is checked against them, and your funds never leave your wallet."
        />

        <div className="container grid max-w-6xl gap-12 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-16">
          <PermissionBoundaryHero />
          <ExecutionRail />
        </div>
      </section>

      {/* ─── CLOSING — hero scale again, the sphere mirrored off the left edge ─── */}
      <section
        id="start"
        className={cn(
          LANDING_SECTION,
          "overflow-hidden lg:flex lg:min-h-[min(40rem,calc(100svh-4rem))] lg:items-center",
        )}
      >
        <div className="hidden lg:absolute lg:inset-0 lg:block lg:overflow-hidden lg:[mask-image:linear-gradient(to_bottom,transparent,black_18%,black_72%,transparent)]">
          <TokenOrbit className="lg:absolute lg:left-[calc(var(--orb)_*_-0.333)] lg:top-1/2 lg:w-[var(--orb)] lg:-translate-y-1/2 lg:[--orb:clamp(44rem,70vw,68rem)]" />
        </div>
        <div className="container relative z-10 max-w-6xl">
          <div className="flex flex-col items-center text-center lg:ml-auto lg:max-w-[min(34rem,46%)] lg:items-start lg:text-left">
            <h2 className="font-heading text-[2.5rem] leading-[0.98] tracking-[-0.04em] text-foreground sm:text-6xl lg:text-[4.5rem]">
              <span className="block text-balance">
                Trade when you want to
                <HeadingDot />
              </span>
              <span className="mt-1 block text-primary">
                Within your limits
                <HeadingDot />
              </span>
            </h2>
            <p className="mt-6 max-w-md font-small text-base leading-relaxed text-muted-foreground sm:text-lg">
              Set your limits once. Every order, yours or an agent&apos;s, is
              checked against them, and your funds stay in your wallet.
            </p>
            <div className="mt-8 w-full max-w-[26.25rem] lg:max-w-[22.5rem]">
              <PrimaryCta isAuthed={isAuthed} />
              {!isAuthed && (
                <p className="mt-4 font-small text-xs text-muted-foreground">
                  Already have an account?{" "}
                  <Link
                    href="/login"
                    className="font-medium text-foreground underline underline-offset-4 transition-colors hover:text-primary"
                  >
                    Log in
                  </Link>
                </p>
              )}
            </div>
            <p className="mt-10 font-small text-xs text-muted-foreground/60">
              Market data and signals are informational, not financial advice.
            </p>
          </div>
        </div>
      </section>

      {/* ─── SIGNAL TAPE — sticky bottom bar; settles above the footer ─── */}
      <LiveActivityTicker className="sticky bottom-0 z-30 border-b-0 pb-[env(safe-area-inset-bottom)]" />
    </div>
  );
}

// Every section below the hero: same width as the hero, roomy rhythm, and a
// hairline that fades out at both ends between sections.
const LANDING_SECTION =
  "relative scroll-mt-24 py-20 sm:py-28 lg:py-32 before:absolute before:inset-x-4 before:top-0 before:mx-auto before:h-px before:max-w-6xl before:bg-gradient-to-r before:from-transparent before:via-border before:to-transparent";

const LANDING_FACTS = [
  { value: "$10", label: "buys a slice of gold or Apple" },
  { value: "24/7", label: "crypto markets never close" },
  { value: "6 checks", label: "before every order" },
  { value: "1 key", label: "and it never leaves your device" },
];

// The hero title's full stop, reused by every section heading.
function HeadingDot() {
  return (
    <>
      <span
        aria-hidden="true"
        className="ml-[0.08em] inline-block size-[0.16em] rounded-full bg-current align-[0.08em]"
      />
      <span className="sr-only">.</span>
    </>
  );
}

// Section header at the hero's width: a two-tone heading in the hero's type on
// one side, the reading copy on the other. `flip` swaps the sides so the page
// alternates the way the hero is asymmetric.
function SectionIntro({
  lead,
  accent,
  body,
  flip = false,
  dot = true,
  children,
}: {
  lead: string;
  accent: string;
  body: string;
  flip?: boolean;
  dot?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <AnimateOnScroll animation="fade-blur">
      <div className="container mb-10 grid max-w-6xl gap-x-16 gap-y-5 lg:mb-14 lg:grid-cols-2 lg:items-end">
        <h2
          className={cn(
            "font-heading text-[2rem] leading-[1.02] tracking-[-0.035em] text-foreground sm:text-4xl lg:text-5xl",
            flip && "lg:order-2",
          )}
        >
          <span className="block">{lead}</span>
          <span className="block text-primary">
            {accent}
            {dot && <HeadingDot />}
          </span>
        </h2>
        <div className={cn("font-small", flip && "lg:order-1")}>
          <p className="max-w-xl text-base leading-relaxed text-muted-foreground">
            {body}
          </p>
          {children}
        </div>
      </div>
    </AnimateOnScroll>
  );
}

// The path an order takes, drawn as a rail that fills in on arrival.
function ExecutionRail() {
  const reduce = useReducedMotion();
  return (
    <div className="font-small">
      <p className="text-sm font-medium text-foreground">
        From signal to settled order
      </p>
      <ol className="relative mt-5 space-y-6">
        <motion.span
          aria-hidden="true"
          className="absolute bottom-4 left-[0.9375rem] top-4 w-px origin-top bg-gradient-to-b from-primary/60 via-border to-transparent"
          initial={reduce ? false : { scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1.2, ease: EASE_OUT }}
        />
        {EXECUTION_STACK.map((step, i) => (
          <motion.li
            key={step.label}
            className="relative flex gap-4"
            initial={reduce ? false : { opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{
              duration: 0.2,
              delay: 0.15 + i * 0.18,
              ease: EASE_OUT,
            }}
          >
            <span
              className="relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-background [&_svg]:size-4"
              style={{ color: step.color }}
              aria-hidden="true"
            >
              {step.icon}
            </span>
            <div className="pt-1">
              <p className="text-sm font-medium text-foreground">
                {step.label}
                <span className="text-muted-foreground"> · {step.value}</span>
              </p>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {step.detail}
              </p>
            </div>
          </motion.li>
        ))}
      </ol>
    </div>
  );
}

// One CTA verb across the page, styled as the login shell's rounded pill.
function PrimaryCta({ isAuthed }: { isAuthed: boolean }) {
  return (
    <Link
      href={isAuthed ? "/dashboard/strategies" : "/register"}
      className="group inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-primary px-6 font-small text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      {isAuthed ? "Open Fexr" : "Create account"}
      <span
        aria-hidden="true"
        className="transition-transform duration-300 group-hover:translate-x-1"
      >
        →
      </span>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MarketsBoard — tokenized stocks (buy in Fexr Wallet, or hand to an agent)
// and crypto (agent only), as plain rows.
// ─────────────────────────────────────────────────────────────────────────────

type Category = "crypto" | "equity" | "commodity";

const EQUITY_SET = new Set([
  "AAPL",
  "MSFT",
  "TSLA",
  "NVDA",
  "AMZN",
  "GOOG",
  "GOOGL",
  "META",
  "NFLX",
  "ORCL",
  "AMD",
  "INTC",
  "PYPL",
  "SQ",
  "HOOD",
  "COIN",
  "SPY",
  "QQQ",
  "IWM",
  "JPM",
  "BAC",
  "V",
  "MA",
  "DIS",
  "UBER",
  "SNAP",
  "RBLX",
  "SOFI",
]);
const COMMODITY_SET = new Set([
  "GLD",
  "SLV",
  "GOLD",
  "SILVER",
  "OIL",
  "WTI",
  "BRENT",
  "WHEAT",
  "CORN",
  "COPPER",
  "PLAT",
  "PALL",
  "XAU",
  "XAG",
  "XAUUSD",
  "XAGUSD",
  "GC",
  "SI",
  "CL",
  "NG",
  "HG",
  "ZW",
  "ZC",
]);

function tickerCat(t: string, serverCat?: string | null): Category {
  if (serverCat === "equity") return "equity";
  if (serverCat === "commodity") return "commodity";
  if (serverCat === "crypto") return "crypto";
  // fallback: name-based guess for older API responses
  const u = t.toUpperCase();
  if (EQUITY_SET.has(u)) return "equity";
  if (COMMODITY_SET.has(u)) return "commodity";
  return "crypto";
}

function toNum(v: string | number | null | undefined): number {
  if (v === null || v === undefined) return 0;
  const n = typeof v === "string" ? parseFloat(v) : v;
  return isNaN(n) ? 0 : n;
}

function fmtPrice(v: number): string {
  if (!v) return "—";
  if (v >= 1000)
    return "$" + v.toLocaleString("en-US", { maximumFractionDigits: 0 });
  if (v >= 1)
    return (
      "$" +
      v.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  return "$" + v.toFixed(4);
}

function fmtPct(v: number): string {
  return (v >= 0 ? "+" : "") + v.toFixed(2) + "%";
}

const BOARD_ROWS = 8;
const BOARD_SORTS = [
  { id: "movers", label: "Movers" },
  { id: "gainers", label: "Gainers" },
  { id: "losers", label: "Losers" },
] as const;
type BoardSort = (typeof BOARD_SORTS)[number]["id"];

const CHAIN_NAMES: Record<number, string> = {
  1: "Ethereum",
  42161: "Arbitrum",
  999: "HyperEVM",
  56: "BNB Chain",
  5000: "Mantle",
};

// CoinGecko platform ids on `onchain` listings, for "Available on …".
const PLATFORM_NAMES: Record<string, string> = {
  ethereum: "Ethereum",
  solana: "Solana",
  "binance-smart-chain": "BNB Chain",
  "arbitrum-one": "Arbitrum",
  base: "Base",
  mantle: "Mantle",
  "polygon-pos": "Polygon",
};

function platformName(id: string) {
  return (
    PLATFORM_NAMES[id] ??
    id.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/** ISO 3166 alpha-2 → flag emoji (regional indicator letters). */
function flagEmoji(code: string) {
  if (!/^[A-Za-z]{2}$/.test(code)) return "";
  return String.fromCodePoint(
    ...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
  );
}

const KIND_LABEL: Partial<Record<TokenizedStock["kind"], string>> = {
  etf: "ETF",
  preferred: "Preferred",
  private: "Pre-IPO",
};

const MARKET_STATUS_LABEL: Record<string, string> = {
  open: "Market open",
  pre_market: "Pre-market",
  after_hours: "After hours",
  closed: "Market closed",
};

// `underlying` is the exchange ticker when the backend matched one, else the
// company name; only a real ticker can seed an agent strategy.
function agentTradable(stock: TokenizedStock) {
  return stock.kind !== "private" && /^[A-Z0-9.]{1,6}$/.test(stock.underlying);
}

function agentHref(symbol: string, isAuthed: boolean) {
  const to = `/dashboard/strategies?asset=${encodeURIComponent(symbol)}`;
  return isAuthed ? to : `/register?from=${encodeURIComponent(to)}`;
}

function fmtAgo(iso: string | null | undefined, now: number): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const mins = Math.max(0, Math.round((now - t) / 60_000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  return `${Math.round(mins / 60)}h ago`;
}

/** The on-chain listing Fexr Wallet can buy, and where it can pay. */
function buyableListing(stock: TokenizedStock) {
  for (const l of stock.listings) {
    if (l.venue !== "swap") continue;
    const chains = l.chains.filter((c) => c.pay_with.length > 0);
    if (chains.length) return { listing: l, chains };
  }
  return null;
}

/** How Fexr Wallet buys it: an on-chain swap first, else Hyperliquid spot. */
function buyRoute(stock: TokenizedStock) {
  const swap = buyableListing(stock);
  if (swap) {
    return {
      symbol: swap.listing.symbol,
      issuer: swap.listing.issuer,
      pay: swap.chains.map(
        (c) =>
          `${c.pay_with.map((p) => p.ticker).join(" or ")} on ${CHAIN_NAMES[c.chain_id] ?? `chain ${c.chain_id}`}`,
      ),
    };
  }
  const hl = stock.listings.find((l) => l.venue === "hyperliquid");
  return hl
    ? { symbol: hl.symbol, issuer: null, pay: ["USDC on Hyperliquid"] }
    : null;
}

function Change({ pct }: { pct: number | null }) {
  if (pct == null || !Number.isFinite(pct))
    return <span className="text-muted-foreground">—</span>;
  return (
    <span className={pct >= 0 ? "text-success" : "text-danger"}>
      {fmtPct(pct)}
    </span>
  );
}

function SegButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "h-7 rounded-md px-2.5 text-xs transition-colors duration-1",
        active
          ? "bg-foreground/[0.06] text-foreground"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

const rowAction =
  "inline-flex h-7 items-center gap-1 rounded-md border border-border px-2.5 text-xs text-muted-foreground transition-colors duration-1 hover:border-primary/40 hover:text-foreground";

// What keeps an agent safe, each enforced by fexrapi or Hyperliquid itself:
// the spending cap (spending_cap_usd), Hyperliquid agent keys that can trade
// but never withdraw, and the pause switch that leaves protective stops on.
const AGENT_GUARDRAILS = [
  "Spends only up to the cap you set",
  "Can trade your account, never withdraw from it",
  "One tap pauses it; protective stops keep running",
];

/**
 * The branded row action: a teal outline that opens the guardrails before
 * anything is set up, so the limits are seen before the click that commits.
 */
function StrategiseAction({
  symbol,
  isAuthed,
}: {
  symbol: string;
  isAuthed: boolean;
}) {
  return (
    <Popover
      onOpenChange={(o) =>
        o &&
        trackEvent("landing.cta_clicked", {
          cta: "agent_card",
          section: "markets",
        })
      }
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-7 items-center gap-1.5 rounded-md border border-primary/50 px-2.5 text-xs font-medium text-primary transition-colors duration-1 hover:border-primary hover:bg-primary/10"
        >
          <ShieldCheck className="hidden h-3.5 w-3.5 sm:block" />
          Strategise
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-4 font-small">
        <p className="text-sm text-foreground">
          An agent trades {symbol} for you
        </p>
        <ul className="mt-3 space-y-2">
          {AGENT_GUARDRAILS.map((g) => (
            <li
              key={g}
              className="flex items-start gap-2 text-xs text-muted-foreground"
            >
              <Check className="mt-px h-3.5 w-3.5 shrink-0 text-success" />
              {g}
            </li>
          ))}
        </ul>
        <Link
          href={agentHref(symbol, isAuthed)}
          onClick={() =>
            trackEvent("landing.cta_clicked", {
              cta: "agent_trade",
              section: "markets",
            })
          }
          className="mt-4 inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md bg-primary text-xs font-medium text-primary-foreground transition-opacity duration-1 hover:opacity-90"
        >
          Set it up
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Buy opens a popover with the stock's `market` One Link: on a phone it opens
 * Fexr Wallet (a swap for on-chain listings, the Hyperliquid spot screen
 * otherwise), on desktop it is a QR to scan. Signed in, the link is issued for the visitor; signed out, the
 * public market dossier carries one.
 */
function BuyAction({ stock }: { stock: TokenizedStock }) {
  const { data: session, status } = useSession();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const buy = buyRoute(stock);

  useEffect(() => {
    if (!open || url || failed) return;
    let cancelled = false;
    const issue =
      status === "authenticated"
        ? getOneLink({ kind: "market", ref: stock.underlying }, session).then(
            (l) => l.url,
          )
        : publicApi
            .get(`/v1/markets/${encodeURIComponent(stock.underlying)}`)
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((d) => d?.link?.url ?? Promise.reject());
    issue
      .then((u: string) => !cancelled && setUrl(brandedLinkUrl(u)))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [open, url, failed, status, session, stock.underlying]);

  if (!buy) return null;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o)
          trackEvent("landing.cta_clicked", {
            cta: "stock_buy",
            section: "markets",
          });
      }}
    >
      <PopoverTrigger asChild>
        <button type="button" className={rowAction}>
          Buy
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-4 font-small">
        <p className="text-sm text-foreground">
          Buy {buy.symbol} in Fexr Wallet
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {buy.issuer ? `${buy.issuer} token tracking ` : ""}
          {stock.name}. Pay with {buy.pay.join(", ")}.
        </p>
        <div className="mt-3 flex items-center gap-3">
          <div className="hidden h-[6.5rem] w-[6.5rem] shrink-0 items-center justify-center rounded-md bg-foreground p-2 sm:flex">
            {url ? (
              <QRCode value={url} size={88} />
            ) : failed ? null : (
              <Loader2 className="h-4 w-4 animate-spin text-background" />
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            <span className="hidden sm:inline">
              Scan with your phone to open it in the app.{" "}
            </span>
            You sign the order with your own key. Fexr can&apos;t move what you
            buy.
          </p>
        </div>
        <a
          href={url ?? FEXR_APP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md bg-primary text-xs font-medium text-primary-foreground transition-opacity duration-1 hover:opacity-90"
        >
          {url ? "Open in Fexr Wallet" : "Get Fexr Wallet"}
          <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </PopoverContent>
    </Popover>
  );
}

function BoardRow({
  icon,
  name,
  meta,
  price,
  change,
  actions,
  muted,
}: {
  icon: React.ReactNode;
  name: string;
  meta: React.ReactNode;
  price: number | null;
  change: number | null;
  actions: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <li
      className={cn(
        "grid grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-3 border-b border-border/60 px-4 py-2.5 last:border-0 sm:grid-cols-[auto_minmax(0,1fr)_6rem_5rem_auto]",
        muted && "opacity-70",
      )}
    >
      {icon}
      <span className="min-w-0">
        <span className="block truncate text-sm text-foreground">{name}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {meta}
        </span>
      </span>
      <span className="hidden text-right text-sm tabular-nums text-foreground sm:block">
        {price ? fmtPrice(price) : "—"}
      </span>
      <span className="text-right text-xs tabular-nums sm:text-sm">
        <Change pct={change} />
      </span>
      <span className="flex justify-end gap-1.5">{actions}</span>
    </li>
  );
}

function StockRows({
  sort,
  country,
  buyableOnly,
  q,
  limit,
  offset,
  isAuthed,
  enabled,
  onMeta,
}: {
  sort: BoardSort;
  country: string | null;
  buyableOnly: boolean;
  q: string;
  limit: number;
  offset: number;
  isAuthed: boolean;
  enabled: boolean;
  onMeta: (d: TokenizedStocksResponse) => void;
}) {
  const { data, error, loading } = useTokenizedStocks({
    sort,
    country,
    buyable: buyableOnly ? true : undefined,
    q,
    limit,
    offset,
    enabled,
  });
  useEffect(() => {
    if (data) onMeta(data);
  }, [data, onMeta]);

  if (loading) return <BoardSkeleton />;
  if (error && !data)
    return (
      <p className="px-4 py-8 text-center text-xs text-muted-foreground">
        Stock prices are unavailable right now.
      </p>
    );
  if (!data?.stocks.length)
    return (
      <p className="px-4 py-8 text-center text-xs text-muted-foreground">
        {q
          ? `No stock matches "${q}"${buyableOnly ? " among those buyable now" : ""}.`
          : "No stocks match. Try all countries or all tracked."}
      </p>
    );
  return (
    <ul className="duration-3 animate-in fade-in-0">
      {data.stocks.map((s) => {
        const buy = buyableListing(s);
        const hl = s.listings.find((l) => l.venue === "hyperliquid");
        const elsewhere = Array.from(
          new Set(
            s.listings
              .filter((l) => l.venue === "onchain")
              .flatMap((l) => l.platforms ?? [])
              .map(platformName),
          ),
        );
        const logo = buy?.chains.find((c) => c.logo_uri)?.logo_uri;
        const kind = KIND_LABEL[s.kind];
        const meta = buy
          ? [
              buy.listing.symbol,
              kind,
              buy.chains
                .map((c) => CHAIN_NAMES[c.chain_id])
                .filter(Boolean)
                .join(" · "),
            ]
          : [
              s.underlying,
              kind,
              hl
                ? `Spot · ${fmtCompact(hl.day_volume_usd ?? 0)} 24h volume`
                : elsewhere.length
                  ? `Available on ${elsewhere.slice(0, 3).join(", ")}`
                  : null,
            ];
        return (
          <BoardRow
            key={s.underlying}
            icon={
              logo ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={logo}
                  alt=""
                  width={28}
                  height={28}
                  className="h-7 w-7 rounded-full"
                />
              ) : (
                <AssetIcon symbol={s.underlying} size={28} />
              )
            }
            name={s.name}
            meta={meta.filter(Boolean).join(" · ")}
            price={s.price}
            change={s.change_24h_pct}
            muted={!buyRoute(s)}
            actions={
              <>
                <BuyAction stock={s} />
                {agentTradable(s) && (
                  <StrategiseAction symbol={s.underlying} isAuthed={isAuthed} />
                )}
              </>
            }
          />
        );
      })}
    </ul>
  );
}

function CryptoRows({
  sort,
  q,
  limit,
  isAuthed,
  inView,
  onUpdated,
}: {
  sort: BoardSort;
  q: string;
  limit: number;
  isAuthed: boolean;
  inView: boolean;
  onUpdated: (at: number) => void;
}) {
  const { ticks, loading } = useSharedTicks(inView);
  useEffect(() => {
    if (ticks.length) onUpdated(Date.now());
  }, [ticks, onUpdated]);
  const rows = useMemo(() => {
    const seen = new Set<string>();
    const needle = q.trim().toLowerCase();
    const crypto = ticks.filter((t) => {
      const key = t.ticker.toUpperCase();
      if (seen.has(key) || tickerCat(t.ticker, t.asset_category) !== "crypto")
        return false;
      seen.add(key);
      if (
        needle &&
        !t.ticker.toLowerCase().includes(needle) &&
        !(t.name ?? "").toLowerCase().includes(needle)
      )
        return false;
      return toNum(t.close) > 0;
    });
    const ch = (t: MarketTick) => toNum(t.price_change_pct_24h);
    crypto.sort((a, b) =>
      sort === "gainers"
        ? ch(b) - ch(a)
        : sort === "losers"
          ? ch(a) - ch(b)
          : Math.abs(ch(b)) - Math.abs(ch(a)),
    );
    return crypto.slice(0, limit);
  }, [ticks, sort, q, limit]);

  if (loading && !rows.length) return <BoardSkeleton />;
  if (!rows.length)
    return (
      <p className="px-4 py-8 text-center text-xs text-muted-foreground">
        {q
          ? `Nothing matches "${q}".`
          : "Crypto prices are unavailable right now."}
      </p>
    );
  return (
    <ul className="duration-3 animate-in fade-in-0">
      {rows.map((t) => {
        const symbol = t.ticker.replace(/\/USD$/i, "").toUpperCase();
        return (
          <BoardRow
            key={t.ticker}
            icon={<AssetIcon symbol={symbol} size={28} />}
            name={t.name || symbol}
            meta={[
              symbol,
              toNum(t.volume) > 0
                ? `${fmtCompact(toNum(t.volume))} 24h volume`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
            price={toNum(t.close)}
            change={
              t.price_change_pct_24h == null
                ? null
                : toNum(t.price_change_pct_24h)
            }
            actions={<StrategiseAction symbol={symbol} isAuthed={isAuthed} />}
          />
        );
      })}
    </ul>
  );
}

function BoardSkeleton() {
  return (
    <ul aria-hidden className="loading-delay">
      {Array.from({ length: BOARD_ROWS }, (_, i) => (
        <li
          key={i}
          className="flex items-center gap-3 border-b border-border/60 px-4 py-2.5 last:border-0"
        >
          <span className="h-7 w-7 rounded-full bg-foreground/[0.05]" />
          <span className="h-3 w-32 rounded bg-foreground/[0.05]" />
        </li>
      ))}
    </ul>
  );
}

function fmtCompact(v: number): string {
  if (!v) return "$0";
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

// The widget opens with BOARD_ROWS and grows by BOARD_MORE up to the API's
// 500; the full page pages through every stock BOARD_PAGE at a time.
const BOARD_MORE = 20;
const BOARD_MAX = 500;
const BOARD_PAGE = 50;
const SEE_ALL_HREF = "/markets/tokenized-stocks";

export function MarketsBoard({ full = false }: { full?: boolean } = {}) {
  const { status } = useSession();
  const isAuthed = status === "authenticated";
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const inView = useInView(ref, { margin: "200px" });
  const [tab, setTab] = useState<"stocks" | "crypto">("stocks");
  const [sort, setSort] = useState<BoardSort>("movers");
  const [country, setCountry] = useState<string | null>(null);
  const [buyableOnly, setBuyableOnly] = useState(!full);
  const [query, setQuery] = useState("");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(full ? BOARD_PAGE : BOARD_ROWS);
  const [page, setPage] = useState(0);
  const [meta, setMeta] = useState<TokenizedStocksResponse | null>(null);
  const [cryptoAt, setCryptoAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // Search waits for a pause in typing.
  useEffect(() => {
    const id = setTimeout(() => setQ(query.trim()), 250);
    return () => clearTimeout(id);
  }, [query]);

  // A new filter starts from the top again.
  useEffect(() => {
    setLimit(full ? BOARD_PAGE : BOARD_ROWS);
    setPage(0);
  }, [full, tab, sort, country, buyableOnly, q]);

  // "/" jumps to search while the board is on screen, unless the visitor is
  // already typing somewhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (
        el &&
        (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))
      )
        return;
      const box = ref.current?.getBoundingClientRect();
      if (!box || box.bottom < 0 || box.top > window.innerHeight) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // `countries` and `issuers` cover every stock whatever the filters, so they
  // also give the radar's totals.
  const allCountries = meta?.countries ?? [];
  const trackedTotal = allCountries.reduce((n, c) => n + c.count, 0);
  const buyableTotal = allCountries.reduce((n, c) => n + (c.buyable ?? 0), 0);
  const countryCount = allCountries.filter((c) => c.country).length;
  // Every country with a tracked stock gets a chip; picking one with nothing
  // buyable widens the board to all tracked.
  const countries = allCountries
    .filter((c) => c.country && c.count > 0)
    .sort(
      (a, b) => (buyableOnly ? b.buyable - a.buyable : 0) || b.count - a.count,
    );
  const issuers = meta?.issuers ?? [];
  const shownIssuers = issuers.slice(0, 5).map((i) => i.issuer);
  const marketStatus = meta?.equity_market_status
    ? (MARKET_STATUS_LABEL[meta.equity_market_status] ??
      meta.equity_market_status.replace(/_/g, " "))
    : null;
  const stamp =
    tab === "stocks"
      ? [marketStatus, meta && `as of ${fmtAgo(meta.as_of, now)}`]
      : [
          cryptoAt &&
            `updated ${fmtAgo(new Date(cryptoAt).toISOString(), now)}`,
        ];
  const offset = full ? page * BOARD_PAGE : 0;
  const total = meta?.total ?? 0;
  const shown = meta?.count ?? 0;
  const canGrow = !full && shown >= limit && limit < Math.min(total, BOARD_MAX);
  const scope = buyableOnly ? "buyable now" : "tracked";

  return (
    <div
      ref={ref}
      className="overflow-hidden rounded-lg border border-border bg-card font-small"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
        <div className="flex flex-wrap items-center gap-1">
          <SegButton active={tab === "stocks"} onClick={() => setTab("stocks")}>
            Stocks
          </SegButton>
          <SegButton active={tab === "crypto"} onClick={() => setTab("crypto")}>
            Crypto
          </SegButton>
          <span className="mx-1.5 h-4 w-px bg-border" />
          {BOARD_SORTS.map((s) => (
            <SegButton
              key={s.id}
              active={sort === s.id}
              onClick={() => setSort(s.id)}
            >
              {s.label}
            </SegButton>
          ))}
        </div>
        <span className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
          <RadarDot size={6} />
          {stamp.filter(Boolean).join(" · ") || "Live"}
        </span>
      </div>

      <label className="flex items-center gap-2 border-b border-border/60 px-4">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          ref={searchRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          maxLength={64}
          aria-label={tab === "stocks" ? "Search stocks" : "Search crypto"}
          placeholder={
            tab === "stocks"
              ? `Search ${trackedTotal > 0 ? `${trackedTotal.toLocaleString()} ` : ""}stocks and ETFs by ticker or name`
              : "Search crypto"
          }
          className="h-10 min-w-0 flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
        />
        {query ? (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Clear search"
            className="text-muted-foreground transition-colors duration-1 hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        ) : (
          <Kbd className="hidden sm:inline-flex">/</Kbd>
        )}
      </label>

      {tab === "stocks" && trackedTotal > 0 && (
        <div className="flex flex-wrap items-center gap-1 border-b border-border/60 px-3 py-1.5">
          <SegButton active={buyableOnly} onClick={() => setBuyableOnly(true)}>
            Buyable now{" "}
            <span className="tabular-nums opacity-60">{buyableTotal}</span>
          </SegButton>
          <SegButton
            active={!buyableOnly}
            onClick={() => setBuyableOnly(false)}
          >
            All tracked{" "}
            <span className="tabular-nums opacity-60">{trackedTotal}</span>
          </SegButton>
          {countries.length > 1 && (
            <>
              <span className="mx-1.5 h-4 w-px bg-border" />
              <SegButton active={!country} onClick={() => setCountry(null)}>
                All countries
              </SegButton>
              {countries.map((c) => (
                <SegButton
                  key={c.country}
                  active={country === c.country}
                  onClick={() => {
                    if (buyableOnly && !c.buyable) setBuyableOnly(false);
                    setCountry(country === c.country ? null : c.country);
                  }}
                >
                  <span
                    aria-hidden
                    className="mr-1.5 align-[-0.15em] text-lg leading-none"
                  >
                    {flagEmoji(c.country!)}
                  </span>
                  {c.country_name ?? c.country}
                  {(buyableOnly ? c.buyable : c.count) > 0 && (
                    <span className="ml-1 tabular-nums opacity-60">
                      {buyableOnly ? c.buyable : c.count}
                    </span>
                  )}
                </SegButton>
              ))}
            </>
          )}
        </div>
      )}

      {tab === "stocks" ? (
        <StockRows
          sort={sort}
          country={country}
          buyableOnly={buyableOnly}
          q={q}
          limit={limit}
          offset={offset}
          isAuthed={isAuthed}
          enabled={inView || full}
          onMeta={setMeta}
        />
      ) : (
        <CryptoRows
          sort={sort}
          q={q}
          limit={limit}
          isAuthed={isAuthed}
          inView={inView}
          onUpdated={setCryptoAt}
        />
      )}

      {tab === "stocks" && meta && total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 px-4 py-2.5 text-xs text-muted-foreground">
          <span className="tabular-nums">
            {full
              ? `${(offset + 1).toLocaleString()}–${(offset + shown).toLocaleString()} of ${total.toLocaleString()} ${q ? `matching "${q}"` : scope}`
              : q
                ? `${total.toLocaleString()} match "${q}"`
                : `Showing ${shown} of ${total.toLocaleString()} ${scope}`}
            {!q && (
              <>
                {" · "}
                {trackedTotal.toLocaleString()} tracked across {countryCount}{" "}
                countries
              </>
            )}
          </span>
          <span className="flex items-center gap-2">
            {full ? (
              <>
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                  className={cn(rowAction, "disabled:opacity-40")}
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={offset + shown >= total}
                  onClick={() => setPage((p) => p + 1)}
                  className={cn(rowAction, "disabled:opacity-40")}
                >
                  Next
                </button>
              </>
            ) : (
              <>
                {canGrow && (
                  <button
                    type="button"
                    onClick={() =>
                      setLimit((l) => Math.min(l + BOARD_MORE, BOARD_MAX))
                    }
                    className={rowAction}
                  >
                    Show more
                  </button>
                )}
                <Link
                  href={SEE_ALL_HREF}
                  onClick={() =>
                    trackEvent("landing.cta_clicked", {
                      cta: "stocks_see_all",
                      section: "markets",
                    })
                  }
                  className="inline-flex items-center gap-1 text-foreground transition-colors duration-1 hover:text-primary"
                >
                  See all
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </>
            )}
          </span>
        </div>
      )}
      {tab === "crypto" && !full && (
        <div className="flex justify-end border-t border-border/60 px-4 py-2.5 text-xs">
          <button
            type="button"
            onClick={() => setLimit((l) => Math.min(l + BOARD_MORE, 200))}
            className={rowAction}
          >
            Show more
          </button>
        </div>
      )}

      <div className="space-y-1.5 border-t border-border/60 px-4 py-3 text-xs text-muted-foreground">
        {shownIssuers.length > 0 && (
          <p>
            <span className="text-foreground">Tokens from</span>{" "}
            {shownIssuers.join(", ")}
            {issuers.length > shownIssuers.length &&
              ` and ${issuers.length - shownIssuers.length} more issuers`}
            . The list refreshes daily as new tokens list.
          </p>
        )}
        <p>
          <span className="text-foreground">Buy</span> stocks with USDC or USDT
          from Fexr Wallet, on-chain or on Hyperliquid. They stay under your own
          key; Fexr can&apos;t move them. Pre-IPO tokens track a private
          company, not a listed share.
        </p>
      </div>
    </div>
  );
}

export type MarketTick = {
  ticker: string;
  name?: string | null;
  close: string | number | null;
  open?: string | number | null;
  high?: string | number | null;
  low?: string | number | null;
  volume?: string | number | null;
  market_cap?: string | number | null;
  market_cap_rank?: number | null;
  fully_diluted_valuation?: string | number | null;
  price_change_pct_1h?: string | number | null;
  price_change_pct_24h: string | number | null;
  price_change_pct_7d?: string | number | null;
  price_change_pct_30d?: string | number | null;
  data_quality?: string | null;
  asset_category?: string | null;
};

const TICK_POLL_MS = 60_000;

let tickCache: MarketTick[] = [];
let tickPromise: Promise<void> | null = null;
let tickTimer: ReturnType<typeof setInterval> | null = null;
let tickViewers = 0;
const tickSubs = new Set<(t: MarketTick[]) => void>();

function pullTicks(): Promise<void> {
  // Coalesce concurrent callers onto one request.
  if (tickPromise) return tickPromise;
  tickPromise = (async () => {
    try {
      const res = await publicApi.get(
        "/v1/analytics/market-data/ticks?limit=200&order=desc",
        { cache: "no-store" },
      );
      if (!res.ok) return;
      const json = await res.json();
      const data: MarketTick[] = Array.isArray(json)
        ? json
        : Array.isArray(json?.data)
          ? json.data
          : [];
      if (data.length === 0) return;
      tickCache = data;
      tickSubs.forEach((fn) => fn(data));
    } catch {
      // Keep the last good tape rather than blanking the widget.
    } finally {
      tickPromise = null;
    }
  })();
  return tickPromise;
}

function useSharedTicks(inView: boolean) {
  const [ticks, setTicks] = useState<MarketTick[]>(tickCache);
  const [loading, setLoading] = useState(tickCache.length === 0);

  useEffect(() => {
    const onTicks = (t: MarketTick[]) => {
      setTicks(t);
      setLoading(false);
    };
    tickSubs.add(onTicks);
    if (tickCache.length > 0) {
      setTicks(tickCache);
      setLoading(false);
    } else {
      void pullTicks().then(() => setLoading(false));
    }
    return () => {
      tickSubs.delete(onTicks);
    };
  }, []);

  useEffect(() => {
    if (!inView) return;
    tickViewers += 1;
    if (!tickTimer) tickTimer = setInterval(pullTicks, TICK_POLL_MS);
    return () => {
      tickViewers -= 1;
      if (tickViewers <= 0 && tickTimer) {
        clearInterval(tickTimer);
        tickTimer = null;
      }
    };
  }, [inView]);

  return { ticks, loading };
}
